/**
 * Centralized route paths for the web shell.
 *
 * Kept in one module so AuthGuard + AuthCallback + LoginPage + the feature
 * screens agree on the same strings without typo drift. T8.3 added the
 * four feature routes — `/home` is kept as the lists alias so T7 redirects
 * still land in the right place.
 */

export const routes = {
  login: '/login',
  // T27.2.10.1 Phase B — the PUBLIC self-serve auth screens. Like /login they
  // sit OUTSIDE AuthGuard (a signed-out user must reach them): `register` is the
  // Create account screen; `verify` consumes an emailed verification token
  // (`/verify/:token`); `resetRequest` is the forgot-password screen; `reset`
  // consumes an emailed reset token (`/reset/:token`). The email deep-links
  // (verify/:token, reset/:token) and the account-exists mail's bare `/reset`
  // link all resolve here.
  register: '/register',
  verify: '/verify',
  resetRequest: '/reset',
  reset: '/reset', // `/reset/:token` for the tokened form; `/reset` for the request form
  oauthCallback: '/auth/callback',
  home: '/home',
  products: '/products',
  offers: '/offers',
  flyerDeals: '/flyerdeals',
  settings: '/settings',
  // T12.1.5.1.1 — the mode-spine shell adds three nav destinations. `plan`
  // is the desktop alias of the planning list (the target T12.1.7.1's
  // "‹ Back to plan" link points at); `deals` is the merged offers/deals
  // surface (T12.1.5.1.3 replaces its content); `trip` is the itinerary
  // screen (T12.1.6 fills it). Bottom-nav "List" points at `home`.
  plan: '/plan',
  deals: '/deals',
  cards: '/cards',
  trip: '/trip',
  // T35.3 / F21 — the Feedback tab destination (report a problem + the
  // household's own past reports; the future open quick question fills the
  // reserved space, T35.4). A real nav destination, not a sheet-only surface.
  feedback: '/feedback',
  // T30.2 — the first-run setup flow (location + selected stores). A manager
  // whose household has no location is routed here by the FirstRunGate; the
  // flow lands the user on `/home` on finish or skip.
  onboarding: '/onboarding',
  // T12.2 — the grouped settings menu's per-surface routes. `/settings` is the
  // menu/landing entry; each drawer entry routes to one of these. Member detail
  // nests under the Account & members surface. Your products reuses `/products`.
  settingsAccount: '/settings/account',
  settingsMemberDetail: '/settings/account/members',
  settingsPreferences: '/settings/preferences',
  settingsBrands: '/settings/brands',
  settingsStores: '/settings/stores',
  // T35.7.2.1 — Billing & subscriptions: the canonical self-manage home for the
  // dev-support donation + backed ideas. Acquisition entry points deep-link here.
  settingsBilling: '/settings/billing',
  settingsApp: '/settings/app',
  // T27.2.10.2 — the PUBLIC legal surface. These five sit OUTSIDE AuthGuard
  // (sibling to `login`) so a logged-out Play reviewer can deep-link them:
  // `/privacy` + `/terms` feed the store-listing Privacy Policy URL, and
  // `/delete-account` feeds the Data Safety "Delete account URL". `/legal` is
  // the index that links the set. Rendered by features/legal/LegalDocPage from
  // the `docs/legal/*.md` drafts (no forked copy).
  legal: '/legal',
  privacy: '/privacy',
  terms: '/terms',
  eula: '/eula',
  cookies: '/cookies',
  deleteAccount: '/delete-account',
} as const;
